import classnames from "classnames"
import { SVGProps, SyntheticEvent } from "react"

import { Icon } from "components/Icon"
import { useToggle } from "ui-kit/components-branded/hooks/useToggle"
import { FAQItem, FAQItems } from "utils/interfaces"

interface ProfileSecondaryItemProps {
    faqItem: FAQItem
}

interface ArrowProps extends SVGProps<SVGSVGElement> {
    fill?: string
}

const Arrow = ({ fill, ...props }: ArrowProps) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            {...props}
        >
            <path
                d="M3.51563 8.46495L12.0006 16.95L20.4856 8.46495L19.0716 7.04995L12.0006 14.122L4.92962 7.04995L3.51563 8.46495Z"
                fill={fill}
            />
        </svg>
    )
}

const MobileArrow = ({ fill, ...props }: ArrowProps) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            {...props}
        >
            <path
                d="M12.0002 15.713L18.0102 9.70302L16.5972 8.28802L12.0002 12.888L7.40423 8.28802L5.99023 9.70202L12.0002 15.713Z"
                fill={fill}
            />
        </svg>
    )
}

const getStyleArrow = (isOn: boolean) => ({
    transform: isOn ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.5s ease",
})

const arrowNormal = (isOn: boolean) => (isOn ? "arrow-up" : "arrow-down")

function ProfileSecondaryItem({ faqItem }: ProfileSecondaryItemProps) {
    const [isOn, toggleIsOn] = useToggle()

    function handleClickItem(event: SyntheticEvent) {
        event.stopPropagation()
        toggleIsOn()
    }

    const styleArrow = getStyleArrow(isOn)

    return (
        <div className="faq__secondary-item" onClick={handleClickItem}>
            <div className="faq__secondary-item-question">
                {faqItem.title}
                <div className="ml-2 is-clickable">
                    <MobileArrow className="small-icon" style={styleArrow} fill="#c2c3c8" />
                    <Arrow className="normal-icon" style={styleArrow} fill="#c2c3c8" />
                </div>
            </div>
            <div
                className={classnames("faq__secondary-item-answer", {
                    "faq__secondary-item-answer-show": isOn,
                })}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="faq__secondary-item-answer-margin">{faqItem.content}</div>
            </div>
        </div>
    )
}

interface ProfileItemProps {
    faqItem: FAQItems
}

export function ProfileItem({ faqItem }: ProfileItemProps) {
    const [isOn, toggleIsOn] = useToggle()

    const profileItemStyles = classnames("faq__item", {
        "faq__item-opened-secondary-item": isOn,
    })

    const styleArrow = getStyleArrow(isOn)

    return (
        <div className={profileItemStyles} onClick={toggleIsOn}>
            <div className="faq__item-question">
                {faqItem.title}
                <div className="ml-2 is-clickable">
                    <MobileArrow className="small-icon" style={styleArrow} fill="#29B981" />
                    <Arrow className="normal-icon" style={styleArrow} fill="#29B981" />
                </div>
            </div>
            <div
                className={classnames("faq__secondary-items", {
                    "faq__secondary-items-show": isOn,
                })}
                onClick={(event) => event.stopPropagation()}
            >
                {faqItem.content.map((item: FAQItem, index: number) => (
                    <ProfileSecondaryItem key={index} faqItem={item} />
                ))}
            </div>
        </div>
    )
}

interface SelectionOfPsychologistsItemProps {
    faqItem: FAQItem
}

export function SelectionOfPsychologistsItem({ faqItem }: SelectionOfPsychologistsItemProps) {
    const [isOn, toggleIsOn] = useToggle()

    return (
        <div className="faq__item" onClick={toggleIsOn}>
            <div className="faq__item-question">
                {faqItem.title}
                <div className="ml-2">
                    <Icon type={arrowNormal(isOn)} size={24} className="small-icon" />
                    <Icon type={arrowNormal(isOn)} size={24} className="normal-icon" />
                </div>
            </div>
            <div
                className={classnames("faq__item-answer", {
                    "faq__item-answer-show": isOn,
                })}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="faq__item-answer-margin">{faqItem.content}</div>
            </div>
        </div>
    )
}

interface FAQProps {
    renderProp: JSX.Element[]
}

export function FAQ({ renderProp, ...props }: FAQProps) {
    return (
        <>
            <div className="faq-headline" {...props}>
                Частые вопросы
            </div>
            <div className="faq">{renderProp}</div>
        </>
    )
}
